import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ClipboardService, IClipboardResponse } from 'ngx-clipboard';
import { EMPTY, forkJoin, of, Subscription } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { ModalComponent } from '../core/components/modal/modal.component';
import {
  ModalType,
  ResourceTableConfig,
} from '../core/models/componentContract.model';
import { TransService } from '../core/models/translation.model';
import { ConfigService } from '../core/services/config.service';
import { ModalService } from '../core/services/modal.service';
import { ResourceService } from '../core/services/resource.service';
import { UtilsService } from '../core/services/utils.service';
import { KdpDiff, KdpService } from '../core/services/kdp.service';
import { AuthMode, Resource } from '../core/models/dataContract.model';
import { IdentityEditorConfig } from '../core/models/editorContract.model';
import { EditorIdentityComponent } from '../core/components/editor-identity/editor-identity.component';

@Component({
  selector: 'app-config-converter',
  templateUrl: './config-converter.component.html',
  styleUrls: ['./config-converter.component.scss'],
  standalone: false
})
export class ConfigConverterComponent implements OnInit, OnDestroy {
  @ViewChild('idpBaseLayer')
  idpBaseLayer: EditorIdentityComponent;

  @ViewChild('idpUiConfiguration')
  idpUiConfiguration: EditorIdentityComponent;

  private subscription: Subscription = new Subscription();

  private attributesToConvert: string[] = [];
  private unwantedTableAttributes = [
    'resources',
    'resolvedQuery',
    'resolvedEditableQuery',
  ];

  // private async asyncStringify(text: string) {
  //   return JSON.stringify(text);
  // }

  public configBefore: string;
  public configAfter: string;

  public configBaseMod: string;
  public configCurrent: string;

  public configBaseModName: string;
  public configModName: string;
  public configName: string;

  public baseLayerResource = {
    dataType: 'Reference',
    displayName: 'key_baseLayer',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'baseLayerResource',
    value: null,
  };
  public uiConfigurationResource = {
    dataType: 'Reference',
    displayName: 'key_uiConfiguration',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'uiConfigurationResource',
    value: null,
  };

  public baseLayerResourceConfig: IdentityEditorConfig = {
    showPhoto: true,
    allowEmptySearch: true,
    queryEmptySearch: `/${this.utils.typeConfiguration}[${this.utils.attObjectType}='uiconfiguration']`,
    queryNormalSearch:
      `/${this.utils.typeConfiguration}[${this.utils.attObjectType}='uiconfiguration' and starts-with(DisplayName,'%SearchText%')]`,
    attributesToShow: [
      {
        field: 'DisplayName',
        width: 0,
        filterable: true,
        filter: 'text',
        sortable: true
      }
    ]
  };
  public uiConfigurationResourceConfig: IdentityEditorConfig = {
    showPhoto: true,
    allowEmptySearch: true,
    queryEmptySearch: `/${this.resource.authenticationMode === AuthMode.azure ? 'xpathtemplate' : 'Set'}[starts-with(${this.utils.attObjectType}, 'ui') and starts-with(${this.utils.attConfiguration}, '%')]`,
    queryNormalSearch:
      `/${this.resource.authenticationMode === AuthMode.azure ? 'xpathtemplate' : 'Set'}[starts-with(${this.utils.attObjectType}, 'ui') and starts-with(${this.utils.attConfiguration}, '%') and starts-with(DisplayName,'%SearchText%')]`,
    attributesToShow: [
      {
        field: 'DisplayName',
        width: 0,
        filterable: true,
        filter: 'text',
        sortable: true
      }
    ]
  };

  public diffResult: KdpDiff | undefined;
  public filteredDiffResult: KdpDiff | undefined;
  public rawDiffResult = '';
  public newModLayerName: string;
  public newUiConfigurationName: string;

  public isLoading = false;
  public showRawData = false;

  private uiConfigXpath = '';
  private uiConfigStaticMembers: string[] = [];

  private adjustResourceTableConfig(editingView: any) {
    if (editingView) {
      if (editingView.components) {
        // dashboard
        editingView.components.forEach((conf: any) => {
          if (
            conf.componentType === 'ResourceTableComponent' &&
            conf.componentConfig &&
            conf.componentConfig.resources
          ) {
            delete conf.componentConfig.resources;
          }
        });
      } else {
        // iteration through types
        Object.getOwnPropertyNames(editingView).forEach(
          (propertyName: string) => {
            if (editingView[propertyName].sections) {
              // iteration through sections
              editingView[propertyName].sections.forEach((section: any) => {
                if (section.attributes) {
                  // iteration through attributes
                  section.attributes.forEach((attribute: any) => {
                    // converter action
                    switch (attribute.editorType) {
                      case 'identity':
                        {
                          if (
                            attribute.editorConfig &&
                            attribute.editorConfig.attributesToShow
                          ) {
                            attribute.editorConfig.attributesToShow.forEach(
                              (ats: any) => {
                                if (ats.name) {
                                  ats.field = ats.name;
                                  delete ats.name;
                                }
                              }
                            );
                          }
                        }
                        break;
                      case 'identities':
                      case 'xpath':
                        {
                          if (attribute.editorConfig) {
                            // direct- / back-link
                            if (
                              attribute.editorConfig.name &&
                              attribute.editorConfig.name
                                .toLowerCase()
                                .startsWith('#directmember-')
                            ) {
                              attribute.editorConfig.linkType = 'directlink';
                              attribute.editorConfig.linkedAttribute =
                                attribute.attributeName;
                            }
                            if (
                              attribute.editorConfig.name &&
                              attribute.editorConfig.name
                                .toLowerCase()
                                .startsWith('#backlinkmember-')
                            ) {
                              attribute.editorConfig.linkType = 'backlink';
                              attribute.editorConfig.linkedAttribute =
                                attribute.attributeName;
                            }
                            // idp config
                            if (
                              attribute.editorConfig.idpConfig &&
                              attribute.editorConfig.idpConfig.attributesToShow
                            ) {
                              attribute.editorConfig.idpConfig.attributesToShow.forEach(
                                (ats: any) => {
                                  if (ats.name) {
                                    ats.field = ats.name;
                                    delete ats.name;
                                  }
                                }
                              );
                            }
                            // table config
                            if (!attribute.editorConfig.tableConfig) {
                              attribute.editorConfig.tableConfig = {};
                            }
                            Object.getOwnPropertyNames(
                              attribute.editorConfig
                            ).forEach((configName: string) => {
                              if (
                                this.attributesToConvert.indexOf(configName) >=
                                0
                              ) {
                                attribute.editorConfig.tableConfig[configName] =
                                  attribute.editorConfig[configName];
                                delete attribute.editorConfig[configName];
                              }
                            });
                            // remove unwanted attributes
                            this.unwantedTableAttributes.forEach(
                              (a: string) => {
                                if (attribute.editorConfig.tableConfig[a]) {
                                  delete attribute.editorConfig.tableConfig[a];
                                }
                              }
                            );
                          }
                        }
                        break;
                      default:
                        break;
                    }
                  });
                }
              });
            }
          }
        );
      }
    }
  }

  canImport = false;

  constructor(
    private modal: ModalService,
    private config: ConfigService,
    private clipboard: ClipboardService,
    private snackbar: MatSnackBar,
    private translate: TransService,
    private utils: UtilsService,
    private resource: ResourceService,
    private kdp: KdpService
  ) { }

  ngOnInit(): void {
    this.canImport = !this.resource.modMode;

    this.attributesToConvert = Object.getOwnPropertyNames(
      new ResourceTableConfig()
    );

    this.subscription.add(
      this.clipboard.copyResponse$.subscribe((res: IClipboardResponse) => {
        if (res.isSuccess) {
          this.snackbar.open(this.translate.instant('key_textCopied'), 'OK', {
            duration: 2000,
          });
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  zipString(data: string) {
    if (data && !data.startsWith('-ZipB64-')) {
      try {
        return '-ZipB64-' + this.utils.zipString(data);
      } catch (error) {
        this.modal.show(
          ModalType.error,
          'key_warning',
          'l10n_failedToCompress'
        );
        return data;
      }
    }
    return data;
  }

  unzipString(data: string) {
    if (data && data.startsWith('-ZipB64-')) {
      try {
        return this.utils.unzipString(data.substring(8));
      } catch (error) {
        this.modal.show(
          ModalType.error,
          'key_warning',
          'l10n_failedToDecompress'
        );
        return data;
      }
    }
    return data;
  }

  copyString(data: string) {
    this.clipboard.copy(data);
  }

  clear(keepCompare = false) {
    if (!keepCompare) {
      // if (this.idpBaseLayer) {
      //   this.idpBaseLayer.value = null;
      // }
      if (this.idpUiConfiguration) {
        this.idpUiConfiguration.value = null;
      }
    }

    this.diffResult = undefined;
    this.filteredDiffResult = undefined;
    this.rawDiffResult = '';
    this.newModLayerName = '';
    this.newUiConfigurationName = '';

    this.uiConfigXpath = '';
    this.uiConfigStaticMembers = [];
  }

  onConvert() {
    setTimeout(() => {
      this.configAfter = '';
    });

    setTimeout(() => {
      let jsonBefore: any = null;
      try {
        jsonBefore = this.configBefore.startsWith('-ZipB64-')
          ? JSON.parse(this.utils.unzipString(this.configBefore.substring(8)))
          : JSON.parse(this.configBefore);
      } catch (error) {
        this.modal.show(
          ModalType.error,
          'key_warning',
          'l10n_inputNotInJsonFormat'
        );
        return;
      }

      if (jsonBefore) {
        jsonBefore.version = this.utils.configVersion;

        if (jsonBefore.dashboard) {
          this.adjustResourceTableConfig(jsonBefore.dashboard);
        }
        if (jsonBefore.editingView) {
          this.adjustResourceTableConfig(jsonBefore.editingView);
        }
        if (jsonBefore.creationView) {
          this.adjustResourceTableConfig(jsonBefore.creationView);
        }
      }

      setTimeout(() => {
        this.configAfter = JSON.stringify(jsonBefore);
      });
    }, this.config.getConfig('intervalSmall', 300));
  }

  onCopy() {
    this.clipboard.copy(this.configAfter);
  }

  onImport() {
    if (!this.canImport) {
      this.modal.show(ModalType.info, 'key_info', 'key_importNotAllowedBecauseOfLayer');
      return;
    }

    let process: MatDialogRef<ModalComponent, any>;

    this.subscription.add(
      this.modal
        .show(
          ModalType.confirm,
          'key_confirmation',
          'key_confirmImportSettings'
        )
        .afterClosed()
        .pipe(
          switchMap((dialogResult: any) => {
            if (dialogResult && dialogResult === 'yes') {
              this.resource.primaryViewSetting = this.configAfter.startsWith(
                '-ZipB64-'
              )
                ? JSON.parse(
                  this.utils.unzipString(this.configAfter.substring(8))
                )
                : JSON.parse(this.configAfter);
              process = this.modal.show(
                ModalType.progress,
                'key_savingChanges',
                '',
                '300px'
              );
              return this.resource.updateUISettings();
            }
            return EMPTY;
          }),
          tap((updateResult: string) => {
            if (updateResult === 'expired') {
              this.modal.show(
                ModalType.error,
                'key_error',
                'key_uiRefreshNeeded'
              );
            } else {
              location.reload();
            }
          }),
          catchError((error: any) => {
            this.modal.show(
              ModalType.error,
              'key_error',
              this.utils.getServiceError(error)
            );
            return EMPTY;
          }),
          finalize(() => {
            if (process) {
              process.close();
            }
          })
        )
        .subscribe()
    );
  }

  onShowRawData() {
    if (this.showRawData) {
      this.rawDiffResult = JSON.stringify(this.filteredDiffResult, null, 2);
    }
  }

  onCompare() {
    if (this.baseLayerResource.value && this.uiConfigurationResource.value) {
      this.isLoading = true;

      const baseLayerId = this.utils.ExtraValue(this.baseLayerResource.value, 'objectid');
      const uiConfigurationId = this.utils.ExtraValue(this.uiConfigurationResource.value, 'objectid');

      let baseLayerSetting = '';
      let uiConfigurationSetting = '';
      this.subscription.add(
        this.resource.getResourceByID(baseLayerId, [this.utils.attConfiguration]).pipe(
          switchMap((baseLayerResult: Resource) => {
            baseLayerSetting = this.utils.ExtraValue(baseLayerResult, this.utils.attConfiguration);
            baseLayerSetting = baseLayerSetting.startsWith('-ZipB64-') ?
              this.utils.unzipString(baseLayerSetting.substring(8)) :
              baseLayerSetting;
            return this.resource.getResourceByID(uiConfigurationId, this.resource.authenticationMode === AuthMode.azure ? [this.utils.attConfiguration, 'staticmembers', 'xpath'] : [this.utils.attConfiguration]);
          }),
          tap((uiConfigurationResult: Resource) => {
            uiConfigurationSetting = this.utils.ExtraValue(uiConfigurationResult, this.utils.attConfiguration);
            uiConfigurationSetting = uiConfigurationSetting.startsWith('-ZipB64-') ?
              this.utils.unzipString(uiConfigurationSetting.substring(8)) :
              uiConfigurationSetting;
            const diff = this.kdp.diff(JSON.parse(baseLayerSetting), JSON.parse(uiConfigurationSetting));
            console.log(diff);
            if (diff) {
              if (this.resource.authenticationMode === AuthMode.azure) {
                this.uiConfigXpath = this.utils.ExtraValue(uiConfigurationResult, 'xpath');
                this.uiConfigStaticMembers = this.utils.ExtraValue(uiConfigurationResult, 'staticmembers');
              }
              this.diffResult = diff;
            } else {
              this.modal.show(ModalType.error, 'key_error', 'key_noChanges');
              return EMPTY;
            }
          }),
          catchError((error: any) => {
            this.modal.show(ModalType.error, 'key_error', this.utils.getServiceError(error));
            return EMPTY;
          }),
          finalize(() => {
            this.isLoading = false;
          })
        ).subscribe()
      )
    }
  }

  onFilteredDiffChange(diff: KdpDiff | undefined) {
    this.filteredDiffResult = diff;
    this.rawDiffResult = JSON.stringify(this.filteredDiffResult, null, 2);
  }

  onConvertToHcConfiguration() {
    if (this.filteredDiffResult && this.newModLayerName && this.newUiConfigurationName) {
      let diffString = JSON.stringify(this.filteredDiffResult);
      diffString = diffString.startsWith('-ZipB64-') ?
        this.utils.unzipString(diffString.substring(8)) :
        diffString;

      try {
        JSON.parse(diffString);
      } catch {
        this.modal.show(ModalType.error, 'key_error', 'key_invalidJson');
        return;
      }

      const modLayerToCreate = {
        DisplayName: this.newModLayerName,
        ObjectType: 'ocgConfiguration',
      }
      modLayerToCreate[this.utils.attConfiguration] = `-ZipB64-${this.utils.zipString(diffString)}`;
      modLayerToCreate[this.utils.attObjectType] = 'uimod';

      const prg = this.modal.show(ModalType.progress, 'key_processing', '', '300px');

      const obs = [];
      obs.push(this.resource.getResourceCount(
        `/ocgConfiguration[displayname='${this.newModLayerName}' and ${this.utils.attObjectType}='uimod']`));
      obs.push(this.resource.getResourceCount(
        `/${this.resource.authenticationMode === AuthMode.azure ? 'xpathtemplate' : 'Set'}[displayname='${this.newUiConfigurationName}' and starts-with(${this.utils.attObjectType}, 'ui')]`));

      this.subscription.add(
        forkJoin(obs).pipe(
          switchMap((existanceResult: Array<number>) => {
            if (existanceResult[0] > 0) {
              this.modal.show(ModalType.error, 'key_error', 'key_modLayerAlreadyExists');
              return EMPTY;
            }
            if (existanceResult[1] > 0) {
              this.modal.show(ModalType.error, 'key_error', 'key_uiConfigAlreadyExists');
              return EMPTY;
            }
            return this.resource.createResource(modLayerToCreate);
          }),
          switchMap((modLayerResult: HttpResponse<any>) => {
            if (modLayerResult.ok) {
              const modLayerId = this.utils.ExtraValue(modLayerResult.body, 'objectid');
              const uiConfigToCreate = {
                DisplayName: this.newUiConfigurationName,
                ObjectType: this.resource.authenticationMode === AuthMode.azure ? 'xpathtemplate' : 'Set',
              }
              if (this.resource.authenticationMode === AuthMode.azure) {
                uiConfigToCreate['name'] = this.newUiConfigurationName.replace(/\s/g, '');
                uiConfigToCreate[this.utils.attTags] = 'uisettings';
                if (this.uiConfigXpath) {
                  uiConfigToCreate['xpath'] = this.uiConfigXpath;
                }
                if (this.uiConfigStaticMembers) {
                  uiConfigToCreate['staticmembers'] = this.uiConfigStaticMembers;
                }
              }
              uiConfigToCreate[this.utils.attObjectType] = 'ui';
              uiConfigToCreate[this.utils.attOrigin] = this.utils.ExtraValue(this.baseLayerResource.value, 'objectid');
              uiConfigToCreate[this.utils.attModList] = {
                0: {
                  id: modLayerId,
                  disabled: false,
                }
              }
              return this.resource.createResource(uiConfigToCreate);
            } else {
              this.modal.show(ModalType.error, 'key_error', 'key_failedToCreateResource');
              return EMPTY;
            }
          }),
          tap((uiConfigResult: HttpResponse<any>) => {
            if (uiConfigResult.ok) {
              this.modal.show(ModalType.info, 'key_info', `${this.translate.instant('key_uiConfigurationCreated')}: ${this.newUiConfigurationName}`);
              this.clear();
            } else {
              this.modal.show(ModalType.error, 'key_error', 'key_failedToCreateResource');
            }
          }),
          catchError((error: any) => {
            this.modal.show(ModalType.error, 'key_error', this.utils.getServiceError(error));
            return EMPTY;
          }),
          finalize(() => {
            if (prg) {
              prg.close();
            }
          })
        ).subscribe()
      );
    }
  }

  convertToMod() {
    if (this.configBaseMod && this.configCurrent && this.configBaseModName && this.configModName && this.configName) {
      try {
        const baseConfigStr = this.configBaseMod.startsWith('-ZipB64-') ?
          this.utils.unzipString(this.configBaseMod.substring(8)) :
          this.configBaseMod;
        const currentConfigStr = this.configCurrent.startsWith('-ZipB64-') ?
          this.utils.unzipString(this.configCurrent.substring(8)) :
          this.configCurrent;
        const baseConfig = JSON.parse(baseConfigStr);
        const currentConfig = JSON.parse(currentConfigStr);
        if (baseConfig && currentConfig) {
          const diff = this.kdp.diff(baseConfig, currentConfig);
          if (diff) {
            let baseConfigId = '';
            let modId = '';

            const prg = this.modal.show(ModalType.progress, 'key_processing', '', '300px');
            const obs = [];
            obs.push(this.resource.getResourceCount(
              `/ocgConfiguration[displayname='${this.configBaseModName}' and ${this.utils.attObjectType}='uiconfiguration']`));
            obs.push(this.resource.getResourceCount(
              `/ocgConfiguration[displayname='${this.configModName}' and ${this.utils.attObjectType}='uimod']`));
            obs.push(this.resource.getResourceCount(
              `/${this.resource.authenticationMode === AuthMode.azure ? 'xpathtemplate' : 'Set'}[displayname='${this.configName}' and starts-with(${this.utils.attObjectType}, 'ui')]`));
            this.subscription.add(
              forkJoin(obs).pipe(
                switchMap((existanceResult: Array<number>) => {
                  if (existanceResult[0] > 0) {
                    this.modal.show(ModalType.error, 'key_error', 'key_baseConfigurationAlreadyExists');
                    return EMPTY;
                  }
                  if (existanceResult[1] > 0) {
                    this.modal.show(ModalType.error, 'key_error', 'key_modAlreadyExists');
                    return EMPTY;
                  }
                  if (existanceResult[2] > 0) {
                    this.modal.show(ModalType.error, 'key_error', 'key_configAlreadyExists');
                    return EMPTY;
                  }
                  return of(null);
                }),
                switchMap(() => {
                  const baseConfigToCreate = {
                    DisplayName: this.configBaseModName,
                    ObjectType: 'ocgConfiguration',
                  }
                  baseConfigToCreate[this.utils.attConfiguration] = this.configBaseMod.startsWith('-ZipB64-') ?
                    this.configBaseMod :
                    '-ZipB64-' + this.utils.zipString(this.configBaseMod);
                  baseConfigToCreate[this.utils.attObjectType] = 'uiconfiguration';
                  return this.resource.createResource(baseConfigToCreate);
                }),
                switchMap((baseConfigResult: HttpResponse<any>) => {
                  if (baseConfigResult.ok) {
                    baseConfigId = this.utils.ExtraValue(baseConfigResult.body, 'objectid');
                    const modToCreate = {
                      DisplayName: this.configModName,
                      ObjectType: 'ocgConfiguration',
                    }
                    modToCreate[this.utils.attConfiguration] = '-ZipB64-' + this.utils.zipString(JSON.stringify(diff));
                    modToCreate[this.utils.attObjectType] = 'uimod';
                    return this.resource.createResource(modToCreate);
                  } else {
                    this.modal.show(ModalType.error, 'key_error', 'key_failedToCreateResource');
                    return EMPTY;
                  }
                }),
                switchMap((modResult: HttpResponse<any>) => {
                  if (modResult.ok) {
                    modId = this.utils.ExtraValue(modResult.body, 'objectid');
                    const uiConfigToCreate = {
                      DisplayName: this.configName,
                      ObjectType: this.resource.authenticationMode === AuthMode.azure ? 'xpathtemplate' : 'Set',
                    }
                    if (this.resource.authenticationMode === AuthMode.azure) {
                      uiConfigToCreate['name'] = this.configName.replace(/\s/g, '');
                      uiConfigToCreate[this.utils.attTags] = 'uisettings';
                    }
                    uiConfigToCreate[this.utils.attObjectType] = 'ui';
                    uiConfigToCreate[this.utils.attOrigin] = baseConfigId;
                    uiConfigToCreate[this.utils.attModList] = {
                      0: {
                        id: modId,
                        disabled: false,
                      }
                    }
                    return this.resource.createResource(uiConfigToCreate);
                  } else {
                    this.modal.show(ModalType.error, 'key_error', 'key_failedToCreateResource');
                    return EMPTY;
                  }
                }),
                tap((uiConfigResult: HttpResponse<any>) => {
                  if (uiConfigResult.ok) {
                    this.modal.show(ModalType.info, 'key_info', `${this.translate.instant('key_uiConfigurationCreated')}: ${this.configName}`);
                    this.configBaseMod = '';
                    this.configCurrent = '';
                    this.configBaseModName = '';
                    this.configModName = '';
                    this.configName = '';
                  } else {
                    this.modal.show(ModalType.error, 'key_error', 'key_failedToCreateResource');
                  }
                }),
                catchError((error: any) => {
                  this.modal.show(ModalType.error, 'key_error', this.utils.getServiceError(error));
                  return EMPTY;
                }),
                finalize(() => {
                  if (prg) {
                    prg.close();
                  }
                })
              ).subscribe()
            );
          } else {
            this.modal.show(ModalType.error, 'key_error', 'key_noChanges');
            return;
          }
        } else {
          this.modal.show(ModalType.error, 'key_error', 'key_invalidJson');
          return;
        }
      } catch (error) {
        this.modal.show(ModalType.error, 'key_error', 'key_invalidJson');
        return;
      }
    }
  }
}
